import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Target, TrendingUp, CheckCircle2,
  Users, DollarSign, BookOpen, GitCompare,
  Link as LinkIcon, FileText, Share2, Eye, Trophy, FileDown, Flame, Phone,
  Bell, BellOff, AlertTriangle, Sparkles,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import logger from '../lib/logger';
import ShareReportCardDialog from '../components/ShareReportCardDialog';
import { DigestHealthWidget } from '../components/DigestHealthWidget';
import PlatformHealthTriageWidget from '../components/home/PlatformHealthTriageWidget';
import OrgHealthTriageWidget from '../components/home/OrgHealthTriageWidget';
import OrgWinsStrip from '../components/home/OrgWinsStrip';
import EmailChannelHealthTile from '../components/admin/EmailChannelHealthTile';
import { TileInfoButton } from '../components/common/TileInfoButton';
import { WhatChangedTile } from '../components/common/WhatChangedTile';
import { RecentlySharedDecksPanel } from '../components/common/RecentlySharedDecksPanel';
import { CoachingCommitmentsTile } from '../components/home/CoachingCommitmentsTile';
import WeakLinksRollupTile from '../components/home/WeakLinksRollupTile';
import CapabilityPulseTile from '../components/home/CapabilityPulseTile';
import DataCompletenessTile from '../components/home/DataCompletenessTile';
import ValueChainTiles from '../components/home/ValueChainTiles';
import { ScenarioComparisonTile } from '../components/home/ScenarioComparisonTile';
import ChainPeerVerdict from '../components/home/ChainPeerVerdict';
import IndustryTagControl from '../components/home/IndustryTagControl';
import { computeChainLeak, STAGE_MATURITY_PILLAR, computeConstraintPillarId, pillarShortFromName } from '../lib/chainLeak';

import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import PinnedReportsSection from '../components/admin/PinnedReportsSection';
import ProactiveInsightsCard from '../components/admin/ProactiveInsightsCard';
import HomeNavigationSection from '../components/home/HomeNavigationSection';
import HomeFooter from '../components/home/HomeFooter';
import CollapsibleSection from '../components/layout/CollapsibleSection';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { useMessaging } from '../hooks/useMessaging';
import { useDemoTools } from '../hooks/useDemoTools';
import { useAlertThreshold } from '../hooks/useAlertThreshold';
import ChainArrow from '../components/layout/ChainArrow';
import MissingMiddleExplainerSheet from '../components/layout/MissingMiddleExplainerSheet';
import MissingMiddleTour from '../components/layout/MissingMiddleTour';
import ChainNode from '../components/home/ChainNode';
import HomeKpiStrip from '../components/home/HomeKpiStrip';
import { DataGapsStrip } from '../components/roe/RoeLeanOverview';
import TopPriorityRail from '../components/home/TopPriorityRail';
import HomePerformanceChart from '../components/home/HomePerformanceChart';
import HomeKeyInsight from '../components/home/HomeKeyInsight';
import HomeRecommendedAction from '../components/home/HomeRecommendedAction';
import HomeRecentAlerts from '../components/home/HomeRecentAlerts';
import { PersonalImpactEntryTile } from '../components/home/PersonalImpactEntryTile';
import { getHealthColor } from '../lib/healthColor';

// ──── TileInfoButton ────
// Tiny info affordance placed next to a card title. Implementation
// lives in `components/common/TileInfoButton.jsx` so other pages can
// reuse it; this file just consumes the import above.


// ──── Main Component ────
export default function LearningIntelligenceEngine() {
  const navigate = useNavigate();
  const { user, viewAsOrg, isSuperAdmin, isOrgAdmin } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  // First name for the personalised "Welcome back" header. Falls back
  // to empty so the greeting stays clean when the name isn't loaded yet.
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const [demoTools, setDemoTools] = useDemoTools();
  const [playbook, setPlaybook] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [alertThreshold, setAlertThreshold] = useAlertThreshold();
  // ── Messaging layer ────────────────────────────────────────────
  // All hero copy is sourced from `config/messaging.json` (defaults)
  // with optional super-admin overrides layered in via Mongo. The
  // brand brief lives in the JSON file — code never carries copy.
  const heroPrefix = useMessaging('hero.headline_prefix', 'Seertech · Mission Control');
  const heroMain = useMessaging('hero.headline_main', 'Navigate the Missing Middle Between Learning and Business Performance');
  const heroDescription = useMessaging(
    'hero.description',
    'Identify where value is leaking, prioritise interventions, execute with confidence, and prove business impact through a continuous intelligence cycle.',
  );
  const systemHealthLabel = useMessaging('health_metrics.overall_label', 'System Health');
  const systemHealthHelp = useMessaging(
    'health_metrics.overall_help',
    'Composite signal across the three engines that determine whether learning is translating into workforce performance: LEI (learning system), PE (people readiness), and ROE (behavior transfer). Engines with no signal yet are excluded so a missing input doesn\u2019t drag the score down.',
  );
  const systemBannerLabel = useMessaging('system_banner.label', 'The Missing Middle Navigation System');
  const valueChainHeaderTooltip = useMessaging(
    'value_chain.header_tooltip',
    "Mission Control's primary portfolio instrument. Tracks how value progresses from learning activity through capability, performance, business outcomes and financial impact — and shows you where it is leaking.",
  );
  const portfolioInterpretation = useMessaging(
    'value_chain.portfolio_interpretation',
    'Your organisation-wide portfolio view. Each stage is a multiplier on the next — wherever the score drops sharpest, that\u2019s where the most value is being lost.',
  );
  // Workflow stage copy — keep `phase` keys (IDENTIFY/MODEL/EXECUTE/PROVE) as
  // code identifiers; the *displayed* phase + label + desc come from messaging.
  const stageFindLabel = useMessaging('workflow.stages.find.label', 'Find');
  const stageFindDesc = useMessaging('workflow.stages.find.question', 'Where is value leaking?');
  const stagePrioritiseLabel = useMessaging('workflow.stages.prioritise.label', 'Prioritise');
  const stagePrioritiseDesc = useMessaging('workflow.stages.prioritise.question', 'Where do we intervene first?');
  const stageActLabel = useMessaging('workflow.stages.act.label', 'Act');
  const stageActDesc = useMessaging('workflow.stages.act.question', 'How do we execute with confidence?');
  const stageProveLabel = useMessaging('workflow.stages.prove.label', 'Prove');
  const stageProveDesc = useMessaging('workflow.stages.prove.question', 'Did it improve performance?');
  // Value-chain copy — hoisted to the top so these hooks are never
  // called conditionally (the chain band now renders inside the
  // Overview tab gate).
  const valueChainTransferTitle = useMessaging('value_chain.transfer_title', 'Value Transfer Chain');
  const valueChainTransferSubtitle = useMessaging('value_chain.transfer_subtitle', 'Track how value transfers from learning to financial impact and identify where it is being constrained.');
  const missingMiddleBracketLabel = useMessaging('value_chain.missing_middle_bracket_label', 'THE MISSING MIDDLE');
  const missingMiddleBracketSub = useMessaging('value_chain.missing_middle_bracket_sub', 'Capability · Confidence · Application · Behavior');
  const behaviorFootnote = useMessaging('value_chain.behavior_footnote', 'BEHAVIOR rolls up Confidence, Application & Behavior from the Missing Middle framework — measured via ROE Health.');
  const [peData, setPeData] = useState(null);
  const [activeHomeTab, setActiveHomeTab] = useState('overview');
  const [showDetails, setShowDetails] = useState(() => {
    try { return localStorage.getItem('mc.showDetails') === '1'; } catch { return false; }
  });
  const [roeData, setRoeData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [peerByStage, setPeerByStage] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [leiHealthScore, setLeiHealthScore] = useState(0);
  // Full pillar payload from /lei-health/overview — drives the
  // footer's Data Confidence signal.
  const [leiPillars, setLeiPillars] = useState(null);
  const [quarterDeltas, setQuarterDeltas] = useState(null);
  // Maturity realization factor — drives the "maturity ceiling"
  // overlay on the chain (how much of each stage's potential is
  // currently realized given the org's maturity band).
  const [maturityFin, setMaturityFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impactRollup, setImpactRollup] = useState(null);
  // Track which live-hot-prospect tokens are "new since last poll"
  // so we can flash them briefly. Keyed by `${case_id}|${last_cta_at}`.
  const [hpFlashKeys, setHpFlashKeys] = useState(new Set());
  // Browser Notification API permission state — surfaces native
  // "ping! Sarah just clicked the CTA" alerts even when the CHRO has
  // the tab open but is skimming another section.
  const [notifyPermission, setNotifyPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'unsupported',
  );
  // Skip notifications on the FIRST poll (initial page-load entries
  // are not "new" — they were already there). Only fire for entries
  // that genuinely arrived while the user was sitting on the dashboard.
  const hpFirstSeenRef = useRef(false);
  // Slack/Teams webhook config UI state (per-user preference).
  const [slackPanelOpen, setSlackPanelOpen] = useState(false);
  const [slackUrl, setSlackUrl] = useState('');
  const [slackUrlSaved, setSlackUrlSaved] = useState('');
  const [slackBusy, setSlackBusy] = useState(false);
  const [slackMsg, setSlackMsg] = useState(null);  // {ok:bool, text:string}
  const [provenance, setProvenance] = useState(null);
  const [healthSeries, setHealthSeries] = useState(null);
  const [healthImports, setHealthImports] = useState([]);
  const [range, setRange] = useState('12m');
  const [roeActuals, setRoeActuals] = useState(null);
  // Chain transfer plan — leader-set expected transfer % per hand-off,
  // org-wide (server-saved). Drives the "below plan" flags on the arrows.
  const [transferPlan, setTransferPlan] = useState({});
  const [planEditorOpen, setPlanEditorOpen] = useState(false);
  const [planDraft, setPlanDraft] = useState({});
  const [planSaving, setPlanSaving] = useState(false);
  const canEditPlan = isSuperAdmin || isOrgAdmin;

  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/modelling-settings/chain-transfer-plan${q}`)
      .then((r) => { if (alive) setTransferPlan(r?.data?.chain_transfer_plan || {}); })
      .catch(() => { if (alive) setTransferPlan({}); });
    return () => { alive = false; };
  }, [viewAsOrg, reloadToken]);

  const CHAIN_HANDOFFS = [
    { key: 'learning_capability', label: 'Learning → Capability' },
    { key: 'capability_confidence', label: 'Capability → Confidence' },
    { key: 'confidence_application', label: 'Confidence → Application' },
    { key: 'application_behavior', label: 'Application → Behavior' },
    { key: 'behavior_outcomes', label: 'Behavior → Business Outcomes' },
    { key: 'outcomes_financial', label: 'Business Outcomes → Financial' },
  ];
  const openPlanEditor = () => { setPlanDraft({ ...transferPlan }); setPlanEditorOpen(true); };
  const savePlan = async () => {
    setPlanSaving(true);
    try {
      const clean = {};
      Object.entries(planDraft).forEach(([k, v]) => {
        if (v !== '' && v != null && !Number.isNaN(Number(v))) clean[k] = Math.round(Number(v));
      });
      const res = await api.post('/modelling-settings/chain-transfer-plan', {
        chain_transfer_plan: clean,
        ...(viewAsOrg?.id ? { organization_id: viewAsOrg.id } : {}),
      });
      setTransferPlan(res?.data?.chain_transfer_plan || clean);
      setPlanEditorOpen(false);
    } catch (err) {
      logger.warn('failed to save chain transfer plan', err);
    } finally {
      setPlanSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/admin/benchmark-baseline/provenance${q}`)
      .then((res) => { if (alive) setProvenance(res?.data?.has_modeled ? res.data : null); })
      .catch(() => { if (alive) setProvenance(null); });
    return () => { alive = false; };
  }, [viewAsOrg, reloadToken]);

  // Persist the "Show details" preference so the global onboarding
  // banner (rendered by DashboardLayout) can honour the same calm-by-
  // default toggle everywhere, and broadcast changes live.
  useEffect(() => {
    try { localStorage.setItem('mc.showDetails', showDetails ? '1' : '0'); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent('mc:show-details', { detail: showDetails })); } catch { /* noop */ }
  }, [showDetails]);

  // Health-score snapshot series → the dark "Performance over time" chart.
  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/health/snapshot-series${q}`)
      .then((res) => { if (alive) setHealthSeries(Array.isArray(res?.data?.series) ? res.data.series : []); })
      .catch(() => { if (alive) setHealthSeries([]); });
    return () => { alive = false; };
  }, [viewAsOrg, reloadToken]);

  // Data-import events → amber markers on the performance chart.
  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/data-management/history${q}`)
      .then((res) => {
        if (!alive) return;
        const rows = Array.isArray(res?.data?.history) ? res.data.history : [];
        setHealthImports(rows
          .filter((r) => r.status !== 'failed' && r.created_at)
          .map((r) => ({ date: r.created_at, label: r.data_type_name || r.data_type || 'Import' })));
      })
      .catch(() => { if (alive) setHealthImports([]); });
    return () => { alive = false; };
  }, [viewAsOrg, reloadToken]);

  // ROE sub-scores (Confidence + Application/L3) → the two extra
  // Missing-Middle chain nodes. Shows '--' until the org has actuals.
  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/roe/actuals/health${q}`)
      .then((res) => { if (alive) setRoeActuals(res?.data || null); })
      .catch(() => { if (alive) setRoeActuals(null); });
    return () => { alive = false; };
  }, [viewAsOrg, reloadToken]);

  // Per-stage sparkline series pulled from the health snapshot history.
  const sparkFor = (k) => (Array.isArray(healthSeries)
    ? healthSeries.map((p) => p[k]).filter((v) => v != null && !Number.isNaN(Number(v)))
    : []);

  // Trend delta for the two Missing-Middle sub-metrics. `quarterDelta`
  // compares the latest snapshot to the one closest to ~90 days earlier
  // so the node peek reads as a true quarter-over-quarter change.
  const quarterDelta = (k) => {
    const s = (Array.isArray(healthSeries) ? healthSeries : []).filter((p) => p && p[k] != null && p.date);
    if (s.length < 2) return undefined;
    const last = s[s.length - 1];
    const target = new Date(last.date); target.setDate(target.getDate() - 90);
    let ref = s[0];
    for (const p of s) { if (new Date(p.date) <= target) ref = p; }
    const d = Number(last[k]) - Number(ref[k]);
    return Math.round(d * 10) / 10;
  };

  // Range-aware header KPIs — derived from the health snapshots inside
  // the shared window so the three chips move with the 30/90/365-day
  // filter (not just the chart).
  const windowKpis = useMemo(() => {
    const days = range === '30d' ? 30 : range === '90d' ? 90 : 365;
    const cutoff = Date.now() - days * 86400000;
    const win = (Array.isArray(healthSeries) ? healthSeries : []).filter((p) => {
      const t = new Date(p.date).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
    const avg = (k) => {
      const vals = win.map((p) => p[k]).filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const comp = avg('composite') != null ? Math.round(avg('composite')) : null;
    const overall = comp == null ? '—' : `${comp}%`;
    const overallTone = comp == null ? 'text-slate-400' : comp >= 70 ? 'text-emerald-400' : comp >= 50 ? 'text-amber-400' : 'text-rose-400';
    // Previous equal-length window (immediately before) for delta badges.
    const prevCut = cutoff - days * 86400000;
    const prev = (Array.isArray(healthSeries) ? healthSeries : []).filter((p) => {
      const t = new Date(p.date).getTime();
      return !Number.isNaN(t) && t >= prevCut && t < cutoff;
    });
    const prevAvg = (k) => {
      const vals = prev.map((p) => p[k]).filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const mkDelta = (curr, before, unit = '') => {
      if (curr == null || before == null) return null;
      const d = Math.round(curr - before);
      return { dir: d > 0 ? 'up' : d < 0 ? 'down' : 'flat', text: `${d > 0 ? '+' : ''}${d}${unit}` };
    };
    const atRiskOf = (pts) => (pts.length ? ['lei', 'pe', 'roe'].filter((k) => {
      const vals = pts.map((p) => p[k]).filter((v) => v != null).map(Number);
      const a = vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : null;
      return a != null && a < 50;
    }).length : 0);
    // Data Confidence = snapshot coverage of the window (count-based so
    // it moves with the range too).
    const expected = Math.max(1, Math.round(days / 30));
    const coverage = win.length / expected;
    const confWord = win.length === 0 ? '—' : coverage >= 1 ? 'High' : coverage >= 0.5 ? 'Medium' : 'Low';
    const confidence = win.length === 0 ? '—' : `${confWord} · ${win.length}`;
    const atRisk = atRiskOf(win);
    return [
      { label: 'Overall Health', value: overall, tone: overallTone, delta: mkDelta(avg('composite'), prevAvg('composite'), 'pt') },
      { label: 'Data Confidence', value: confidence, tone: 'text-[#44b6d2]', delta: mkDelta(win.length, prev.length) },
      { label: 'At Risk', value: String(atRisk), tone: 'text-amber-400', delta: mkDelta(atRisk, atRiskOf(prev)) },
    ];
  }, [healthSeries, range]);


  useEffect(() => {
    const fetchHealth = async () => {
      setLoading(true);
      const orgParam = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
      try {
        const [peRes, roeRes, roiRes, leiRes, rollupRes, deltaRes, matRes] = await Promise.allSettled([
          api.get(`/people-engine/health${orgParam}`),
          api.get(`/roe/actuals/health${orgParam}`),
          api.get(`/scenarios/roi-summary${orgParam}`),
          api.get(`/lei-health/overview${orgParam}`),
          api.get(`/people-engine/impact-rollup${orgParam}`),
          api.get(`/health/quarter-deltas${orgParam}`),
          api.get(`/maturity-assessment/financial${orgParam}`),
        ]);
        if (peRes.status === 'fulfilled') setPeData(peRes.value.data);
        if (roeRes.status === 'fulfilled') setRoeData(roeRes.value.data);
        if (roiRes.status === 'fulfilled') setRoiData(roiRes.value.data);
        if (leiRes.status === 'fulfilled') {
          setLeiHealthScore(Math.round(leiRes.value.data?.overall_score || 0));
          if (Array.isArray(leiRes.value.data?.pillars)) {
            setLeiPillars(leiRes.value.data.pillars);
          }
        }
        if (rollupRes.status === 'fulfilled') setImpactRollup(rollupRes.value.data);
        if (deltaRes.status === 'fulfilled') setQuarterDeltas(deltaRes.value.data);
        if (matRes.status === 'fulfilled') setMaturityFin(matRes.value.data);

        // Build the per-stage industry peer standing for the home
        // "vs peer" verdict. Each health endpoint now ships an
        // industry-aware peer_benchmark (percentile + median).
        const peers = {};
        const leiPeer = leiRes.status === 'fulfilled' ? leiRes.value.data?.peer_benchmark : null;
        const pePeer = peRes.status === 'fulfilled' ? peRes.value.data?.current?.peer_benchmark : null;
        const roePeer = roeRes.status === 'fulfilled' ? roeRes.value.data?.peer_benchmark : null;
        const roiPeer = roiRes.status === 'fulfilled' ? roiRes.value.data?.peer_benchmark : null;
        if (leiPeer) peers.learning = leiPeer;
        if (pePeer) peers.capability = pePeer;
        if (roePeer) peers.behavior = roePeer;
        if (roiPeer) peers.financial = roiPeer;
        setPeerByStage(Object.keys(peers).length ? peers : null);

        // Persist today's snapshot for tomorrow's delta calc.
        // Fire-and-forget; failure here must never block the home
        // page render. The endpoint is idempotent per (org, date).
        try {
          const cur = peRes.status === 'fulfilled' ? peRes.value.data?.current : null;
          const roeBlock = roeRes.status === 'fulfilled' ? roeRes.value.data : null;
          const roiBlock = roiRes.status === 'fulfilled' ? roiRes.value.data : null;
          const leiOverall = leiRes.status === 'fulfilled' ? leiRes.value.data?.overall_score : null;
          // Confidence & Application (L3) live on a separate endpoint —
          // pull them so their history accrues alongside the others and
          // their node sparklines become live trends.
          let confidenceVal = null; let applicationVal = null;
          try {
            const aq = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
            const actualsRes = await api.get(`/roe/actuals/health${aq}`);
            confidenceVal = actualsRes?.data?.motivation?.confidence_avg ?? null;
            applicationVal = actualsRes?.data?.l3?.actual ?? null;
          } catch { /* optional — snapshot still persists other metrics */ }
          await api.post('/health/snapshots', {
            lei: leiOverall != null ? Number(leiOverall) : null,
            pe: cur?.pe != null ? Number(cur.pe) * 100 : null,
            roe: roeBlock?.roe_actual?.pct != null ? Number(roeBlock.roe_actual.pct) : null,
            kpi: roeBlock?.pe?.validated_pct != null ? Number(roeBlock.pe.validated_pct) : null,
            roi: roiBlock?.roi_percent != null ? Number(roiBlock.roi_percent) : null,
            confidence: confidenceVal != null ? Number(confidenceVal) : null,
            application: applicationVal != null ? Number(applicationVal) : null,
          });
        } catch (snapErr) {
          logger.debug?.('snapshot post failed', snapErr);
        }
      } catch (err) {
        logger.error('Failed to fetch health data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHealth();
  }, [viewAsOrg, reloadToken]);

  // Load existing Slack/Teams webhook so the UI reflects the saved state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/users/notification-preferences');
        if (cancelled) return;
        const url = r?.data?.preferences?.slack_webhook_url || '';
        setSlackUrl(url);
        setSlackUrlSaved(url);
      } catch (err) {
        logger.warn('failed to load slack webhook prefs', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveSlackWebhook = async () => {
    setSlackBusy(true);
    setSlackMsg(null);
    try {
      await api.put('/users/notification-preferences', {
        slack_webhook_url: slackUrl.trim(),
      });
      setSlackUrlSaved(slackUrl.trim());
      setSlackMsg({ ok: true, text: slackUrl.trim() ? 'Webhook saved.' : 'Webhook cleared.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save webhook.';
      setSlackMsg({ ok: false, text: detail });
    } finally {
      setSlackBusy(false);
    }
  };

  const handleTestSlackWebhook = async () => {
    setSlackBusy(true);
    setSlackMsg(null);
    try {
      const r = await api.post('/users/notification-preferences/test-slack-webhook');
      const platform = r?.data?.platform === 'teams' ? 'Teams' : 'Slack';
      setSlackMsg({ ok: true, text: `Test ${platform} alert delivered. Check the channel.` });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Test delivery failed.';
      setSlackMsg({ ok: false, text: detail });
    } finally {
      setSlackBusy(false);
    }
  };

  const handleSetHotProspectOutcome = async (token, outcome) => {
    if (!token) return;
    try {
      await api.post(`/people-engine/hot-prospects/${token}/outcome`, { outcome });
      // Optimistic local update; the next 30s poll will reconcile.
      setImpactRollup((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        next.live_hot_prospects = (prev.live_hot_prospects || []).map((hp) =>
          hp.token === token
            ? { ...hp, outcome, outcome_set_at: new Date().toISOString() }
            : hp,
        );
        // Recompute conversion preview optimistically.
        if (prev.hot_prospect_conversion) {
          const prevOutcome = (prev.live_hot_prospects || []).find((hp) => hp.token === token)?.outcome;
          const oc = { ...prev.hot_prospect_conversion.outcomes };
          if (prevOutcome) oc[prevOutcome] = Math.max(0, (oc[prevOutcome] || 0) - 1);
          else oc.unmarked = Math.max(0, (oc.unmarked || 0) - 1);
          if (outcome) oc[outcome] = (oc[outcome] || 0) + 1;
          else oc.unmarked = (oc.unmarked || 0) + 1;
          const marked = ['followed_up', 'meeting_booked', 'no_response']
            .reduce((s, k) => s + (oc[k] || 0), 0);
          next.hot_prospect_conversion = {
            ...prev.hot_prospect_conversion,
            outcomes: oc,
            marked_count: marked,
            conversion_rate: marked > 0
              ? Math.round(((oc.meeting_booked || 0) / marked) * 1000) / 1000
              : null,
          };
        }
        return next;
      });
    } catch (err) {
      logger.warn('failed to set hot-prospect outcome', err);
    }
  };

  // 30-second polling JUST for the Live Hot Prospects feed. Pauses  // automatically when the tab is hidden so we don't burn API calls
  // while the dashboard isn't on screen.
  useEffect(() => {
    const orgParam = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    const pollFeed = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const r = await api.get(`/people-engine/impact-rollup${orgParam}`);
        const next = r.data;
        setImpactRollup((prev) => {
          if (!prev) return next;
          const prevKeys = new Set(
            (prev.live_hot_prospects || []).map((hp) => `${hp.case_id}|${hp.last_cta_at}`),
          );
          const nextHps = next.live_hot_prospects || [];
          const newOnes = nextHps
            .map((hp) => `${hp.case_id}|${hp.last_cta_at}`)
            .filter((k) => !prevKeys.has(k));
          if (newOnes.length > 0) {
            setHpFlashKeys((cur) => {
              const merged = new Set(cur);
              newOnes.forEach((k) => merged.add(k));
              return merged;
            });
            // Drop the flash after 6s.
            setTimeout(() => {
              setHpFlashKeys((cur) => {
                const next2 = new Set(cur);
                newOnes.forEach((k) => next2.delete(k));
                return next2;
              });
            }, 6000);

            // Native browser notification — only after the first poll
            // (otherwise we'd ping for entries that were already on
            // screen when the user opened the dashboard) AND only when
            // permission is granted.
            if (
              hpFirstSeenRef.current
              && typeof window !== 'undefined'
              && 'Notification' in window
              && Notification.permission === 'granted'
            ) {
              try {
                // Match each new key back to its hot-prospect entry.
                const newEntries = nextHps.filter((hp) =>
                  newOnes.includes(`${hp.case_id}|${hp.last_cta_at}`),
                );
                newEntries.forEach((hp) => {
                  const skill = hp.skill_name || hp.title || 'a report card';
                  const n = new Notification(
                    `🔥 Hot prospect: ${hp.recipient_label || 'A stakeholder'}`,
                    {
                      body: `Just clicked the CTA on "${skill}". The room is warm — pick up the phone.`,
                      tag: `hp-${hp.case_id}`,
                      icon: '/favicon.ico',
                      requireInteraction: false,
                    },
                  );
                  n.onclick = () => {
                    try {
                      window.focus();
                      n.close();
                    } catch (_) { /* ignore */ }
                  };
                });
              } catch (e) {
                logger.warn('hot-prospect notification failed', e);
              }
            }
          }
          // After the very first poll completes we flip this flag so
          // every subsequent diff can fire native alerts.
          hpFirstSeenRef.current = true;
          return next;
        });
      } catch (err) {
        logger.warn('Live hot-prospect poll failed', err);
      }
    };
    const id = setInterval(pollFeed, 30000);
    return () => clearInterval(id);
  }, [viewAsOrg]);

  // Extract scores
  const pePct = Math.round((peData?.current?.pe || 0) * 100);
  const leiPct = leiHealthScore; // Use LEI Health endpoint (pillar-weighted, matches LEI Dashboard)
  const roePct = roeData?.roe_actual?.pct || 0;
  const peValidPct = roeData?.pe?.validated_pct || 0;

  // Missing-Middle constraint — the middle stage (Capability →
  // Confidence → Application → Behavior) with the steepest step-down
  // from the stage immediately before it. Uses current scores so all
  // four middle nodes can flag even without quarter-over-quarter
  // history. Only stages that actually dropped qualify; largest wins.
  const confidenceScore = Math.round(roeActuals?.motivation?.confidence_avg || 0);
  const applicationScore = Math.round(roeActuals?.l3?.actual || 0);
  const MIDDLE_STAGE_META = {
    capability:  { label: 'Capability', path: '/people-engine' },
    confidence:  { label: 'Confidence', path: '/roe-health' },
    application: { label: 'Application', path: '/roe-health' },
    behavior:    { label: 'Behavior', path: '/roe-health' },
  };
  const middleStep = [
    { key: 'capability', drop: leiPct - pePct, ok: leiPct > 0 && pePct > 0 },
    { key: 'confidence', drop: pePct - confidenceScore, ok: pePct > 0 && confidenceScore > 0 },
    { key: 'application', drop: confidenceScore - applicationScore, ok: confidenceScore > 0 && applicationScore > 0 },
    { key: 'behavior', drop: applicationScore - roePct, ok: applicationScore > 0 && roePct > 0 },
  ].filter((s) => s.ok && s.drop > 0).sort((a, b) => b.drop - a.drop)[0] || null;
  const middleConstraint = middleStep?.key || null;
  // Unified constraint object so the Top Priority rail, Key Insight and
  // Recommended Action all tell the SAME story as the chain's PRIMARY
  // CONSTRAINT badge (score-based step-down within the Missing Middle,
  // not quarter-over-quarter deltas).
  const constraint = middleStep ? {
    key: middleConstraint,
    stage: MIDDLE_STAGE_META[middleConstraint],
    dropPts: Math.round(middleStep.drop),
  } : null;
  // How long the current stage has been the constraint — count the
  // trailing run of snapshots whose biggest middle step-down matches
  // today's constraint, so "improvement" (a shorter streak) is visible.
  const rowConstraint = (r) => {
    if (!r) return null;
    const steps = [
      ['capability', (r.lei ?? 0) - (r.pe ?? 0), r.lei != null && r.pe != null],
      ['confidence', (r.pe ?? 0) - (r.confidence ?? 0), r.pe != null && r.confidence != null],
      ['application', (r.confidence ?? 0) - (r.application ?? 0), r.confidence != null && r.application != null],
      ['behavior', (r.application ?? 0) - (r.roe ?? 0), r.application != null && r.roe != null],
    ].filter((s) => s[2] && s[1] > 0).sort((a, b) => b[1] - a[1]);
    return steps[0]?.[0] || null;
  };
  const constraintStreakMonths = (() => {
    if (!middleConstraint || !Array.isArray(healthSeries) || healthSeries.length === 0) return 0;
    let startIdx = null;
    for (let i = healthSeries.length - 1; i >= 0; i--) {
      if (rowConstraint(healthSeries[i]) === middleConstraint) startIdx = i; else break;
    }
    if (startIdx === null) return 0;
    const last = healthSeries[healthSeries.length - 1];
    const start = healthSeries[startIdx];
    if (!last?.date || !start?.date) return 0;
    const days = (new Date(last.date) - new Date(start.date)) / 86400000;
    return Math.max(0, Math.round(days / 30));
  })();

  // Load the org-wide alert threshold from the backend (falls back to
  // the local value) and let admins persist changes for all users.
  useEffect(() => {
    let alive = true;
    const q = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    api.get(`/modelling-settings/alert-threshold${q}`)
      .then((r) => { if (alive && r?.data?.alert_threshold != null) setAlertThreshold(r.data.alert_threshold); })
      .catch(() => {});
    return () => { alive = false; };
  }, [viewAsOrg, setAlertThreshold]);

  const saveOrgAlertThreshold = (val) => {
    const v = Math.max(0, Math.min(100, Math.round(val)));
    setAlertThreshold(v); // optimistic + local
    api.post('/modelling-settings/alert-threshold', {
      alert_threshold: v,
      ...(viewAsOrg?.id ? { organization_id: viewAsOrg.id } : {}),
    }).catch(() => {});
  };

  // AI fix-plan — asks the narrative engine for tailored actions for the
  // constrained stage, shown alongside the data-driven steps.
  const generateAiPlan = async () => {
    if (!playbook) return;
    setAiLoading(true); setAiPlan(null);
    try {
      const rootName = constraintPillarId ? (maturityPillarById[constraintPillarId]?.name || constraintShort) : null;
      const res = await api.post('/insights/stage-fix-plan', {
        stage: playbook.stage.label,
        drop_pts: playbook.dropPts,
        prev_stage: CONSTRAINT_PLAYBOOK[playbook.key]?.prev,
        root_cause: rootName,
      });
      setAiPlan(Array.isArray(res.data?.actions) ? res.data.actions : []);
    } catch (e) {
      setAiPlan(['Could not generate an AI plan right now — please try again.']);
    } finally {
      setAiLoading(false);
    }
  };
  // Focused fix-plan copy per Missing-Middle stage — surfaced in the
  // constraint playbook panel when the badge is clicked.
  const CONSTRAINT_PLAYBOOK = {
    capability: { prev: 'Learning', actions: [
      'Audit which capabilities aren\u2019t forming from completed learning \u2014 look for content that\u2019s consumed but never practised.',
      'Add manager-led practice or coaching checkpoints right after the key modules.',
      'Tighten the link between learning objectives and the specific on-the-job capabilities they should build.',
    ] },
    confidence: { prev: 'Capability', actions: [
      'Pinpoint where confidence drops after capability is built \u2014 usually missing safe practice or reinforcement.',
      'Introduce low-stakes application opportunities so people rehearse before it counts.',
      'Have managers explicitly signal that applying the new skill is expected \u2014 and safe.',
    ] },
    application: { prev: 'Confidence', actions: [
      'Remove the workflow blockers stopping confident people from applying the skill on the job.',
      'Set clear application expectations and bake the new behaviour into day-to-day work.',
      'Add manager nudges at the exact moments application should be happening.',
    ] },
    behavior: { prev: 'Application', actions: [
      'Reinforce the behaviour so early application actually sticks over time.',
      'Build recognition and accountability around the sustained behaviour.',
      'Track leading indicators so backsliding is caught and corrected early.',
    ] },
  };

  // Build org query param for navigation
  const orgNav = viewAsOrg?.id ? `organization_id=${viewAsOrg.id}` : '';
  const navTo = (path, extra = '') => {
    const params = [orgNav, extra].filter(Boolean).join('&');
    navigate(params ? `${path}?${params}` : path);
  };

  // Build the `modeled` prop for a chain node from benchmark provenance.
  const modeledFor = (streamIds) => {
    if (!provenance?.streams) return null;
    const streams = streamIds
      .map((id) => provenance.streams[id] ? { id, ...provenance.streams[id] } : null)
      .filter(Boolean);
    if (!streams.some((s) => s.modeled)) return null;
    return {
      streams,
      context: {
        industry_label: provenance.industry_label,
        size_band_label: provenance.size_band_label,
        confidence: provenance.confidence,
        formula: provenance.formula,
      },
      onOpenBenchmark: () => navTo('/benchmark-baseline'),
    };
  };
  const roiPct = roiData?.roi_percent || 0;
  const roiMultiplier = roiPct > 0 ? (roiPct / 100).toFixed(1) : 0;

  // Plan nudges — once the live chain values and a saved transfer plan are
  // both available, report the 6 hand-off efficiencies to the backend. It
  // fires an in-app nudge to org leaders on the FIRST slip below plan (and
  // is idempotent, so re-posting on reload never double-notifies).
  const planNudgeSent = useRef(false);
  useEffect(() => {
    if (loading || planNudgeSent.current) return;
    if (!transferPlan || Object.keys(transferPlan).length === 0) return;
    const conf = Math.round(roeActuals?.motivation?.confidence_avg || 0);
    const l3 = Math.round(roeActuals?.l3?.actual || 0);
    const roiBand = parseFloat(roiMultiplier) >= 3 ? 80 : parseFloat(roiMultiplier) >= 1 ? 55 : parseFloat(roiMultiplier) > 0 ? 30 : 0;
    const eff = (from, to) => (from > 0 ? Math.round((to / from) * 100) : null);
    const efficiencies = {
      learning_capability: eff(leiPct, pePct),
      capability_confidence: eff(pePct, conf),
      confidence_application: eff(conf, l3),
      application_behavior: eff(l3, roePct),
      behavior_outcomes: eff(roePct, peValidPct),
      outcomes_financial: eff(peValidPct, roiBand),
    };
    planNudgeSent.current = true;
    api.post('/modelling-settings/chain-transfer-plan/evaluate', {
      efficiencies,
      ...(viewAsOrg?.id ? { organization_id: viewAsOrg.id } : {}),
    }).catch((err) => logger.warn('plan nudge evaluate failed', err));
  }, [loading, transferPlan, roeActuals, leiPct, pePct, roePct, peValidPct, roiMultiplier, viewAsOrg]);


  // Live value leak — the chain stage with the steepest quarter-over-
  // quarter drop. Single source of truth (lib/chainLeak) shared with
  // the per-page ChainPositionStrip and the guided tour. Used to
  // pulse the matching ChainNode below.
  const chainLeak = computeChainLeak(quarterDeltas?.metrics);
  // Embedded chain callout — rendered directly above the leaking
  // node inside the Value Transfer Chain so the node IS the leak
  // banner (no separate value-leak strip anymore).
  const leakCalloutText = chainLeak
    ? `${chainLeak.stage.label} is reducing value transfer by ${Math.abs(Math.round(chainLeak.deltaPct))}%.`
    : null;

  // Maturity ceiling factor — fraction of potential each stage is
  // currently realizing given the org's maturity band (e.g. 0.78).
  // Drives the faint "headroom capped by maturity" overlay on every
  // ChainNode. Null/1 → no overlay (no assessment, or fully realized).
  const maturityRealizedPct = maturityFin?.has_assessment
    ? (maturityFin?.investment_efficiency?.current_pct
        ?? (maturityFin?.maturity_factor?.current != null ? maturityFin.maturity_factor.current * 100 : null))
    : null;
  const maturityCeilingFactor = (typeof maturityRealizedPct === 'number' && maturityRealizedPct > 0)
    ? maturityRealizedPct / 100
    : null;

  // Pillar attribution — which maturity pillar is most responsible
  // for capping each stage's headroom, so the chain can tint the
  // headroom band by pillar and flag the binding constraint.
  const maturityPillarScores = maturityFin?.pillar_scores || [];
  const maturityPillarById = Object.fromEntries(maturityPillarScores.map((p) => [p.pillar_id, p]));
  const constraintPillarId = computeConstraintPillarId(maturityPillarScores);
  const headroomPillarFor = (stageKey) => {
    if (!maturityCeilingFactor) return null;
    const meta = STAGE_MATURITY_PILLAR[stageKey];
    if (!meta) return null;
    const p = maturityPillarById[meta.pillarId];
    return {
      short: pillarShortFromName(p?.name, meta.short),
      name: p?.name || meta.short,
      score: p?.score != null ? Math.round(p.score) : null,
      tint: meta.tint,
      tick: meta.tick,
      text: meta.text,
      isConstraint: meta.pillarId === constraintPillarId,
    };
  };
  // Binding constraint summary for the legend.
  const constraintMeta = constraintPillarId
    ? Object.values(STAGE_MATURITY_PILLAR).find((m) => m.pillarId === constraintPillarId)
    : null;
  const constraintShort = constraintPillarId
    ? pillarShortFromName(maturityPillarById[constraintPillarId]?.name, constraintMeta?.short)
    : null;
  const constraintScore = constraintPillarId && maturityPillarById[constraintPillarId]?.score != null
    ? Math.round(maturityPillarById[constraintPillarId].score)
    : null;

  // Composite risk score
  const scores = [leiPct, pePct, roePct].filter(s => s > 0);
  const compositeRisk = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const compositeHealth = getHealthColor(compositeRisk);

  // Share dialog state for Recent Report Cards
  const [shareTarget, setShareTarget] = useState(null);

  // Per-card "Export to PowerPoint" downloading state — avoids double-clicks.
  const [exportingId, setExportingId] = useState(null);
  const handleExportPptx = async (caseId, skillName) => {
    if (exportingId) return;
    setExportingId(caseId);
    try {
      const res = await api.get(
        `/people-engine/report-card/${caseId}/export-pptx`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const safe = (skillName || 'report_card')
        .replace(/[^a-zA-Z0-9_\- ]/g, '').trim().replace(/\s+/g, '_') || 'report_card';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}_QBR_slide.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('export-pptx-failed', err);
    } finally {
      setExportingId(null);
    }
  };

  // Quarterly All-In Impact Deck download (multi-slide).
  const [downloadingDeck, setDownloadingDeck] = useState(false);
  const handleDownloadQuarterlyDeck = async () => {
    if (downloadingDeck) return;
    setDownloadingDeck(true);
    try {
      const orgParam = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
      const res = await api.get(`/people-engine/quarterly-deck${orgParam}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      // Pull filename from content-disposition (server-authored).
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || 'Impact_Deck.pptx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('quarterly-deck-failed', err);
    } finally {
      setDownloadingDeck(false);
    }
  };

  // One-click branded PDF snapshot of the live Mission Control home.
  // Turns any live demo into a leave-behind asset without first
  // pinning anything — captures System Health, the Impact Chain
  // stages, the biggest value leak, and the peer standing.
  const [downloadingSnapshot, setDownloadingSnapshot] = useState(false);
  const handleDownloadSnapshot = async () => {
    if (downloadingSnapshot) return;
    setDownloadingSnapshot(true);
    try {
      const orgParam = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
      const res = await api.get(`/home/snapshot-pdf${orgParam}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m?.[1] || 'Mission_Control_Snapshot.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('home-snapshot-failed', err);
    } finally {
      setDownloadingSnapshot(false);
    }
  };

  const __pageTheme = getLightSectionTheme('LEI');
  // Light hero theme — matches the Option A body. The closed-loop
  // pipeline reads on a slate-50 canvas with a thin teal accent rule
  // and a faint indigo/teal radial glow (rendered inside the hero
  // children below). Text tokens shift to slate so the title +
  // description sit naturally on the lighter ground.
  const __homeHeroTheme = {
    ...__pageTheme,
    heroGradient: 'bg-slate-50',
    bodyTint: 'bg-slate-50',
    textBase: 'text-slate-900',
    textLight: 'text-slate-800',
    textLighter: 'text-slate-600',
    // Title — Seertech-logo cyan, weighted heavier than the default
    // `font-bold` so it carries more presence on the lighter canvas
    // without going back to a dark, heavy slate.
    titleClass: 'text-[#44b6d2] font-extrabold',
    actionBtn: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50',
    statBg: 'bg-white hover:bg-slate-50',
  };
  return (
    <PageShell theme={{ bodyTint: isDark ? 'bg-[#0a0f1c]' : 'bg-slate-50' }} stickyDisabled>
    <div className={cn('space-y-6 text-slate-100', !isDark && 'mc-light')} data-testid="learning-intelligence-engine">

      {/* ── Minimal page header (on the dark canvas) — replaces the
          old full PageHero. Welcome + subtitle on the left, live KPI
          chips + Share-as-PDF on the right. ── */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pt-1">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-extrabold text-slate-100 tracking-tight" data-testid="home-welcome-heading">
            Welcome back{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="text-sm text-slate-400 mt-1">Here's what's happening across your organization.</p>
        </div>
        <div className="flex items-center gap-4">
          {activeHomeTab === 'overview' && (
            <div className="inline-flex rounded-lg border border-[#1f2a44] bg-[#111827] p-0.5" data-testid="home-range-filter" title="Shared time window">
              {[
                { id: '30d', label: '30 days' },
                { id: '90d', label: '90 days' },
                { id: '12m', label: '12 months' },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRange(r.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${range === r.id ? 'bg-[#44b6d2] text-[#04222b]' : 'text-slate-400 hover:text-slate-200'}`}
                  data-testid={`home-range-${r.id}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {activeHomeTab === 'overview' && (
            <div className="flex items-stretch gap-3">
              <HomeKpiStrip items={windowKpis} />
              <DataGapsStrip variant="tile" selectedOrg={viewAsOrg?.id} navigate={navigate} />
            </div>
          )}
        </div>
      </div>

      {/* ── Sub-navigation tabs (on the dark canvas) ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1f2a44]" data-testid="home-subnav">
        <div className="flex flex-wrap gap-1">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'reports', label: 'Portfolio & Reports' },
            { id: 'health', label: 'Operations & Health' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveHomeTab(t.id)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${activeHomeTab === t.id ? 'border-[#44b6d2] text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
              data-testid={`home-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 pr-1">
          <button
            type="button"
            onClick={handleDownloadSnapshot}
            disabled={downloadingSnapshot}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            data-testid="download-home-snapshot-btn"
            title="Download a branded PDF snapshot of this dashboard"
          >
            <FileDown className="h-3.5 w-3.5 text-[#44b6d2]" />
            {downloadingSnapshot ? 'Generating…' : 'Share as PDF'}
          </button>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="text-xs text-slate-400 hover:text-slate-200"
            data-testid="home-show-details-toggle"
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
          <button
            type="button"
            onClick={() => setDemoTools(!demoTools)}
            className="text-xs text-slate-400 hover:text-slate-200"
            data-testid="home-demo-tools-toggle"
            title="Show or hide the demo tools and the guided-tour launcher"
          >
            {demoTools ? 'Hide demo tools' : 'Demo tools'}
          </button>
        </div>
      </div>

      {activeHomeTab === 'overview' && (
      <div className="space-y-6" data-testid="home-overview-dark">

        {/* Row — Value Transfer Chain (8) + Top Priority rail (4) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-8 flex">

      {/* ── Value Transfer Chain — Mission Control Instrument Panel ──
          The single hero of the page. The constrained stage glows
          orange with an embedded ACTIVE VALUE CONSTRAINT callout so
          the chain itself answers both WHERE VALUE FLOWS and WHERE IT
          IS BEING LOST. */}
      <div className="w-full rounded-2xl bg-[#0f1729] p-5 md:p-6 shadow-xl ring-1 ring-[#22304d] flex flex-col" data-testid="learning-impact-chain-band">
        {/* Compact executive header — single tight title row +
            one supporting line. All earlier explanatory bands
            (narrative + value-flow reference) collapsed into the
            tooltip behind the info icon per the UX brief. */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-base font-bold tracking-[0.18em] text-white uppercase flex items-center gap-2" data-testid="learning-impact-chain-title">
            {valueChainTransferTitle}
            <TileInfoButton
              testid="learning-impact-chain-info"
              title="Value Transfer Chain"
              body={valueChainHeaderTooltip}
            />
          </h2>
          <div className="flex flex-col items-end gap-1">
            <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold whitespace-nowrap">
              Live · Portfolio View
            </span>
            <span className="hidden lg:inline text-[10px] text-slate-500 tracking-wide whitespace-nowrap">
              {missingMiddleBracketSub}
            </span>
            {canEditPlan && (
              <button
                type="button"
                onClick={openPlanEditor}
                className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-[#22304d] bg-[#0b1220] px-2 py-0.5 text-[10px] font-semibold text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors"
                data-testid="chain-plan-edit-btn"
                title="Set the expected transfer % for each hand-off"
              >
                <Target className="h-3 w-3 text-cyan-400" /> Transfer plan
              </button>
            )}
          </div>
        </div>
        {/* ── The Missing Middle bracket ──────────────────────────────
            A single graduated line spanning exactly the four middle
            stages — Capability · Confidence · Application · Behavior.
            No downward end-tips; the line is brightest at the centre
            (next to the label) and fades toward each tip. Nudged up 5px
            so it clears the Primary-Constraint badge when that badge
            lands on a middle-stage node. Mirrors the chain row's flex
            geometry (7 flex-1 nodes + 6 fixed w-5 arrows) so it lines up
            over those node columns. Also restores the tour target. */}
        <div className="flex items-center gap-2 h-3 mb-1 -translate-y-[5px] select-none" aria-hidden="true" data-testid="missing-middle-bracket">
          {/* LEARNING column + first arrow — outside the middle */}
          <div className="flex-1 min-w-0" />
          <div className="w-5 flex-shrink-0" />
          {/* CAPABILITY — bracket start (dimmest tip) */}
          <div className="flex-1 min-w-0 h-[2px] rounded-full bg-cyan-400/15" />
          <div className="w-5 flex-shrink-0 h-[2px] bg-cyan-400/30" />
          {/* CONFIDENCE */}
          <div className="flex-1 min-w-0 h-[2px] bg-cyan-400/55" />
          {/* centre arrow — brightest, carries the label */}
          <div className="w-5 flex-shrink-0 h-[2px] bg-cyan-400/90 relative">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-[#0f1729] px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">
              {missingMiddleBracketLabel}
            </span>
          </div>
          {/* APPLICATION */}
          <div className="flex-1 min-w-0 h-[2px] bg-cyan-400/55" />
          <div className="w-5 flex-shrink-0 h-[2px] bg-cyan-400/30" />
          {/* BEHAVIOR — bracket end (dimmest tip) */}
          <div className="flex-1 min-w-0 h-[2px] rounded-full bg-cyan-400/15" />
          {/* trailing arrow + BUSINESS OUTCOMES + arrow + FINANCIAL — outside */}
          <div className="w-5 flex-shrink-0" />
          <div className="flex-1 min-w-0" />
          <div className="w-5 flex-shrink-0" />
          <div className="flex-1 min-w-0" />
        </div>
        <div className={cn("flex items-start gap-2", constraint && "mt-14")} data-testid="learning-impact-chain-row">
          <ChainNode
            label="LEARNING" sublabel="Engaged" score={leiPct} icon={BookOpen} color="slate" statusHint="How actively your people are engaging with the learning on offer."
            onClick={() => navTo('/lei-health')}
            isLeak={false}
            modeled={modeledFor(['lei'])}
            leakCallout={leakCalloutText}
            ceilingFactor={maturityCeilingFactor}
            headroomPillar={headroomPillarFor('learning')}
            deltaPct={quarterDeltas?.metrics?.lei?.delta_pct}
            deltaDirection={quarterDeltas?.metrics?.lei?.direction}
            spark={sparkFor('lei')}
            onPeekClick={() => navTo('/score-trends')}
            info={{
              title: 'LEARNING — LEI Health (Learning System)',
              body: 'Learning Effectiveness Index. Composite score of how well your learning content, programs, and assessments are performing — completion, mastery, content health, assessment integrity. The further upstream a problem is, the cheaper it is to fix; this is your earliest warning indicator.',
              linkLabel: 'Open Learning Health',
              onLink: () => navTo('/lei-health'),
            }}
          />
          <ChainArrow
            fromScore={leiPct} toScore={pePct}
            expected={transferPlan.learning_capability}
            pulse={(leiPct - pePct) > 0 && (leiPct - pePct) === Math.max(leiPct - pePct, pePct - roePct, roePct - peValidPct, peValidPct - (parseFloat(roiMultiplier) >= 3 ? 80 : parseFloat(roiMultiplier) >= 1 ? 55 : parseFloat(roiMultiplier) > 0 ? 30 : 0))}
            testId="chain-arrow-learning-to-capability"
          />
          <ChainNode
            label="CAPABILITY" sublabel="Developed" score={pePct} icon={Users} color="purple" statusHint="How much real capability that learning is building."
            onClick={() => navTo('/people-engine')}
            isLeak={middleConstraint === 'capability'}
            onConstraintClick={() => constraint && setPlaybook(constraint)}
            modeled={modeledFor(['capability', 'retention'])}
            leakCallout={leakCalloutText}
            ceilingFactor={maturityCeilingFactor}
            headroomPillar={headroomPillarFor('capability')}
            deltaPct={quarterDeltas?.metrics?.pe?.delta_pct}
            deltaDirection={quarterDeltas?.metrics?.pe?.direction}
            spark={sparkFor('pe')}
            onPeekClick={() => navTo('/score-trends')}
            info={{
              title: 'CAPABILITY — PE Health (People Engine)',
              body: 'Combines capability, motivation, and retention into a single people-readiness score. PE answers "do you have the right people, willing and able, to convert learning into performance?" When LEI is healthy but PE is at-risk, your training is fine but your workforce isn\'t absorbing it.',
              linkLabel: 'Open Capability Health',
              onLink: () => navTo('/people-engine'),
            }}
          />
          <ChainArrow
            fromScore={pePct} toScore={Math.round(roeActuals?.motivation?.confidence_avg || 0)}
            expected={transferPlan.capability_confidence}
            testId="chain-arrow-capability-to-confidence"
          />
          <ChainNode
            label="CONFIDENCE" sublabel="Confident" score={Math.round(roeActuals?.motivation?.confidence_avg || 0)} icon={CheckCircle2} isLeak={middleConstraint === 'confidence'} statusHint="How confident people feel to actually use what they learned." deltaPct={quarterDelta('confidence')} onConstraintClick={() => constraint && setPlaybook(constraint)}
            onClick={() => navTo('/roe-health')}
            spark={sparkFor('confidence')}
            emptyHint="Connect L2/L3 data"
            onPeekClick={() => navTo('/roe-health')}
          />
          <ChainArrow
            fromScore={Math.round(roeActuals?.motivation?.confidence_avg || 0)} toScore={Math.round(roeActuals?.l3?.actual || 0)}
            expected={transferPlan.confidence_application}
            testId="chain-arrow-confidence-to-application"
          />
          <ChainNode
            label="APPLICATION" sublabel="Applied" score={Math.round(roeActuals?.l3?.actual || 0)} icon={Target} isLeak={middleConstraint === 'application'} statusHint="How much of it they actually apply on the job." deltaPct={quarterDelta('application')} onConstraintClick={() => constraint && setPlaybook(constraint)}
            onClick={() => navTo('/roe-health')}
            spark={sparkFor('application')}
            emptyHint="Connect L2/L3 data"
            onPeekClick={() => navTo('/roe-health')}
          />
          <ChainArrow
            fromScore={Math.round(roeActuals?.l3?.actual || 0)} toScore={roePct}
            expected={transferPlan.application_behavior}
            testId="chain-arrow-application-to-behavior"
          />
          <ChainNode
            label="BEHAVIOR" sublabel="Sustained" score={roePct} icon={GitCompare} color="teal" statusHint="How well the new behaviour sticks over time."
            onClick={() => navTo('/roe-health')}
            isLeak={middleConstraint === 'behavior'}
            onConstraintClick={() => constraint && setPlaybook(constraint)}
            modeled={modeledFor(['motivation'])}
            leakCallout={leakCalloutText}
            ceilingFactor={maturityCeilingFactor}
            headroomPillar={headroomPillarFor('behavior')}
            deltaPct={quarterDeltas?.metrics?.roe?.delta_pct}
            deltaDirection={quarterDeltas?.metrics?.roe?.direction}
            spark={sparkFor('roe')}
            onPeekClick={() => navTo('/score-trends')}
            info={{
              title: 'BEHAVIOR — ROE Health (Return on Expectation)',
              body: 'Measures how much of the learning is actually transferring into observed on-the-job behavior. Rolls up the Missing Middle\'s Confidence, Application and Behavior stages — the three steps inside the middle of the value chain that are usually invisible. Compares pre/post performance scores against a "behavior transfer" baseline. A weak ROE with strong LEI/PE means people learned it, but didn\'t do it differently.',
              linkLabel: 'Open Behavior Health',
              onLink: () => navTo('/roe-health'),
            }}
          />
          <ChainArrow
            fromScore={roePct} toScore={peValidPct}
            expected={transferPlan.behavior_outcomes}
            pulse={(roePct - peValidPct) > 0 && (roePct - peValidPct) === Math.max(leiPct - pePct, pePct - roePct, roePct - peValidPct, peValidPct - (parseFloat(roiMultiplier) >= 3 ? 80 : parseFloat(roiMultiplier) >= 1 ? 55 : parseFloat(roiMultiplier) > 0 ? 30 : 0))}
            testId="chain-arrow-performance-to-outcomes"
          />
          <ChainNode
            label="BUSINESS OUTCOMES" sublabel="Business Impact" score={peValidPct} icon={TrendingUp} color="emerald" statusHint="How much that behaviour moves real business results."
            onClick={() => navTo('/score-trends', 'tab=kpi')}
            isLeak={false}
            modeled={modeledFor(['kpi'])}
            leakCallout={leakCalloutText}
            ceilingFactor={maturityCeilingFactor}
            headroomPillar={headroomPillarFor('outcomes')}
            deltaPct={quarterDeltas?.metrics?.kpi?.delta_pct}
            deltaDirection={quarterDeltas?.metrics?.kpi?.direction}
            spark={sparkFor('composite')}
            info={{
              title: 'BUSINESS OUTCOMES — KPI Impact (Business Metrics)',
              body: 'Percentage of measurable business KPIs (e.g. time-to-productivity, quality, NPS, safety incidents) that have moved in the right direction since the linked training was deployed. This is the first place training shows up in the language the C-suite already speaks.',
              linkLabel: 'Open Score Trends',
              onLink: () => navTo('/score-trends', 'tab=kpi'),
            }}
          />
          <ChainArrow
            fromScore={peValidPct} toScore={parseFloat(roiMultiplier) >= 3 ? 80 : parseFloat(roiMultiplier) >= 1 ? 55 : parseFloat(roiMultiplier) > 0 ? 30 : 0}
            expected={transferPlan.outcomes_financial}
            pulse={false}
            testId="chain-arrow-outcomes-to-financial"
          />
          <ChainNode
            label="FINANCIAL IMPACT" sublabel="ROI" score={parseFloat(roiMultiplier)} suffix="x" icon={DollarSign} color="amber" statusHint="The dollar return generated for every dollar invested."
            onClick={() => navTo('/roi-health')}
            isLeak={false}
            modeled={modeledFor(['kpi_value', 'kpi'])}
            leakCallout={leakCalloutText}
            ceilingFactor={maturityCeilingFactor}
            headroomPillar={headroomPillarFor('financial')}
            deltaPct={quarterDeltas?.metrics?.roi?.delta_pct}
            deltaDirection={quarterDeltas?.metrics?.roi?.direction}
            info={{
              title: 'FINANCIAL IMPACT — ROI (Financial Return)',
              body: 'Multiplier of dollar value returned per dollar invested in learning. Uses each business case\'s certified financial impact and divides by program cost. Above 3.0× is healthy, 1.0–3.0× is at-risk, below 1.0× is critical (you\'re burning money). This is the bottom-line CFO answer.',
              linkLabel: 'Open Financial Health',
              onLink: () => navTo('/roi-health'),
            }}
          />
        </div>

        <button
          type="button"
          onClick={() => navTo('/score-trends')}
          className="group/vfa mt-5 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
          data-testid="view-full-value-transfer"
        >
          View full value transfer analysis
          <ArrowRight className="h-4 w-4 transition-transform group-hover/vfa:translate-x-0.5" />
        </button>
        {Object.keys(transferPlan).length > 0 && (
          <p className="mt-2 text-[10px] text-slate-500" data-testid="chain-plan-legend">
            <span className="text-rose-400 font-bold">▼</span> = transfer below your plan for that hand-off.
          </p>
        )}
      </div>

      {/* Transfer-plan editor — org-wide expected transfer % per hand-off */}
      <Sheet open={planEditorOpen} onOpenChange={setPlanEditorOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="chain-plan-editor">
          <SheetHeader>
            <SheetTitle>Value transfer plan</SheetTitle>
            <SheetDescription>
              Set the expected transfer efficiency (% of value that carries through) for each hand-off.
              The chain flags any hand-off running below plan. Leave blank to skip. Saved for the whole organization.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-3">
            {CHAIN_HANDOFFS.map((h) => (
              <div key={h.key} className="flex items-center justify-between gap-3">
                <label className="text-sm text-slate-700 dark:text-slate-300" htmlFor={`plan-${h.key}`}>{h.label}</label>
                <div className="flex items-center gap-1">
                  <input
                    id={`plan-${h.key}`}
                    type="number" min={0} max={300}
                    value={planDraft[h.key] ?? ''}
                    onChange={(e) => setPlanDraft((p) => ({ ...p, [h.key]: e.target.value }))}
                    className="w-20 text-right text-sm border rounded px-2 py-1 bg-white text-slate-900"
                    placeholder="—"
                    data-testid={`chain-plan-input-${h.key}`}
                  />
                  <span className="text-xs text-slate-400">%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPlanEditorOpen(false)} data-testid="chain-plan-cancel">Cancel</Button>
            <Button size="sm" onClick={savePlan} disabled={planSaving} className="bg-[#44b6d2] text-[#04222b] hover:bg-[#3aa5c0]" data-testid="chain-plan-save">
              {planSaving ? 'Saving…' : 'Save plan'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
          </div>

          {/* Top Priority rail */}
          <div className="lg:col-span-4 flex">
            <TopPriorityRail
              hasLeak={!!constraint}
              stageLabel={constraint?.stage?.label}
              leakPct={constraint ? constraint.dropPts : 0}
              streak={constraintStreakMonths}
              rootCauseLabel={constraintPillarId ? `${maturityPillarById[constraintPillarId]?.name || constraintShort}${constraintScore != null ? ` (${constraintScore})` : ''}` : null}
              recoveryPct={constraint ? Math.max(1, Math.round(constraint.dropPts * (maturityCeilingFactor || 1))) : 0}
              primaryLabel={constraint ? 'View recommended actions' : 'Explore Navigation Analysis'}
              onPrimary={() => { if (constraint) { const [p, q] = constraint.stage.path.split('?'); navTo(p, q || ''); } else { navTo('/lei-dashboard'); } }}
            />
          </div>
        </div>

        {/* Row — Performance over time (6) + Key Insight (3) + Recommended Action (3) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <HomePerformanceChart series={healthSeries} imports={healthImports} range={range} />
          <HomeKeyInsight
            text={constraint
              ? `Value is leaking most at ${constraint.stage.label} — a ${constraint.dropPts}-point step-down from the stage before it.${constraintShort ? ` Closing the ${constraintShort} constraint would recover the most value.` : ''}`
              : compositeRisk > 0
                ? `System health sits at ${compositeRisk}% (${compositeHealth.label}). No single stage is dragging the chain — focus on widening data coverage to sharpen the signal.`
                : 'Connect more engine data to unlock trend insights across the value chain.'}
            metric={compositeRisk > 0 ? {
              label: 'Overall System Health',
              value: `${compositeRisk}%`,
              sub: compositeHealth.label,
              tone: compositeRisk >= 80 ? 'text-emerald-400' : compositeRisk >= 50 ? 'text-amber-400' : 'text-rose-400',
            } : null}
            onMetricClick={() => {
              if (constraint) { const [p, q] = constraint.stage.path.split('?'); navTo(p, q || ''); return; }
              const stages = [
                { pct: leiPct, path: '/lei-health' },
                { pct: pePct, path: '/people-engine' },
                { pct: roePct, path: '/roe-health' },
                { pct: peValidPct, path: '/roi-health' },
              ].filter((s) => s.pct > 0).sort((a, b) => a.pct - b.pct);
              navTo(stages[0] ? stages[0].path : '/lei-dashboard');
            }}
          />
          <HomeRecommendedAction
            title={constraint ? `Open ${constraint.stage.label} Health` : 'Explore AI Insights'}
            onAction={() => { if (constraint) { const [p, q] = constraint.stage.path.split('?'); navTo(p, q || ''); } else { navTo('/lei-dashboard'); } }}
          />
        </div>

        {/* Recent Alerts strip */}
        <HomeRecentAlerts
          stages={[
            { label: 'Learning', score: leiPct },
            { label: 'Capability', score: pePct },
            { label: 'Behavior', score: roePct },
            { label: 'Business Outcomes', score: peValidPct },
          ]}
          atRisk={(impactRollup && impactRollup.top_gaps) ? impactRollup.top_gaps.length : 0}
          threshold={alertThreshold}
          canEdit={isOrgAdmin || isSuperAdmin}
          onThresholdChange={saveOrgAlertThreshold}
        />
      </div>
      )}

      {/* ── Overview deep-dive (behind "Show details") ─────────────
          One-line nudges + the full navigation-condition diagnostics
          + recommended-next-actions grid live here so the default
          Overview stays calm. */}
      {activeHomeTab === 'overview' && <ValueChainTiles orgId={viewAsOrg?.id} />}

      {activeHomeTab === 'overview' && showDetails && (
        <div className={cn('space-y-6', isDark && 'home-dark-scope')} data-testid="home-overview-details">
          <CapabilityPulseTile />
          <DataCompletenessTile />
          <HomeNavigationSection orgId={viewAsOrg?.id} />
        </div>
      )}

      {/* ── PROOF & REPORTING ── everything that *proves* impact or
          packages it for stakeholders, folded into one collapsed
          strip so the homepage stays navigation-first. */}
      {activeHomeTab === 'reports' && (
      <CollapsibleSection
        storageKey="home.proofReporting"
        eyebrow="Prove"
        title="Proof & Reporting"
        defaultExpanded
        onDark
        description="Pinned reports, impact rollups, engagement analytics and live stakeholder activity."
        accent="text-emerald-600"
        testId="home-proof-reporting"
      >
      <div className={cn('space-y-8', isDark && 'home-dark-scope')}>
      <ProactiveInsightsCard surface="home" />
      <PinnedReportsSection surface="home" title="Pinned reports" />
      <ScenarioComparisonTile />
      <WhatChangedTile />
      <RecentlySharedDecksPanel />

      {/* Impact Rollup — CEO-at-a-glance: active gaps + quarter delta + recent report cards */}
      {impactRollup && (impactRollup.top_gaps?.length > 0 || impactRollup.recent_reports?.length > 0) && (
        <div data-testid="impact-rollup-widget">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Impact Rollup</h2>
              <p className="text-xs text-slate-500">Active gaps being worked, cumulative delta this quarter, and the most recent executive report cards.</p>
            </div>
            <div className="flex items-center gap-2">
              {(impactRollup.quarter_impact?.reports_posted || 0) > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadQuarterlyDeck}
                  disabled={downloadingDeck}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
                  data-testid="download-quarterly-deck-btn"
                  title={`Download a multi-slide PowerPoint with every report card posted this ${impactRollup.quarter_impact?.quarter || 'quarter'}`}
                >
                  <FileDown className="h-3.5 w-3.5" />
                  {downloadingDeck ? 'Generating…' : `Download ${impactRollup.quarter_impact?.quarter || 'Q'} Deck`}
                </button>
              )}
              <span className="text-xs text-slate-400">{impactRollup.quarter_impact?.quarter}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Top gaps */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="h-4 w-4 text-rose-500" />
                  Top Active Gaps
                  <TileInfoButton
                    testid="rollup-gaps-info"
                    title="Top Active Gaps"
                    body="The three skills where measured proficiency is furthest below your required level right now, ranked by gap size. Click a row to open the full Intervention Backlog and drill into the playbook already in flight. A gap shrinks here as matching report cards lift post-training proficiency toward the target."
                    linkLabel="Open Intervention Backlog"
                    onLink={() => navigate('/people-engine/intervention-backlog')}
                  />
                </CardTitle>
                <p className="text-[11px] text-slate-500 mt-0.5" data-testid="rollup-gaps-subtext">
                  The 3 most urgent skill gaps right now — click a row to open the full backlog.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {impactRollup.top_gaps?.length > 0 ? (
                  impactRollup.top_gaps.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => navigate('/people-engine/intervention-backlog')}
                      className="w-full text-left p-2 rounded-md hover:bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors"
                      data-testid={`rollup-gap-${g.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{g.skill_name}</span>
                        <span className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 tabular-nums flex-shrink-0">
                          gap {g.gap?.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <span className="tabular-nums">{g.achieved_level?.toFixed(1)} / {g.required_level?.toFixed(1)}</span>
                        <span className="text-slate-300">·</span>
                        <span className="capitalize">{g.status?.replace('_', ' ')}</span>
                        {g.linked_program_name && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1 text-violet-700 truncate">
                              <LinkIcon className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{g.linked_program_name}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic py-2">No active gaps — you're caught up.</p>
                )}
              </CardContent>
            </Card>

            {/* Quarter impact */}
            <Card className="border-violet-100 bg-gradient-to-br from-white to-violet-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-violet-500" />
                  Quarter Impact
                  <TileInfoButton
                    testid="rollup-quarter-info"
                    title="Quarter Impact"
                    body="Net proficiency delta = the sum of (post-training level − pre-training level) across every report card posted this quarter with measurable before/after scores. The Before/After averages show where you started and ended. Use this as your headline CFO-ready answer to 'what did L&D move this quarter?'"
                    linkLabel="Open Business Case Builder"
                    onLink={() => navigate('/business-case-builder')}
                  />
                </CardTitle>
                <p className="text-[11px] text-slate-500 mt-0.5" data-testid="rollup-quarter-subtext">
                  How much proficiency your training moved this quarter, in aggregate.
                </p>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-extrabold tabular-nums text-slate-900" data-testid="rollup-net-delta">
                  {impactRollup.quarter_impact?.net_delta == null ? '—' :
                    (impactRollup.quarter_impact.net_delta > 0 ? '+' : '') + impactRollup.quarter_impact.net_delta.toFixed(2)}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Net proficiency delta across {impactRollup.quarter_impact?.reports_with_delta || 0} measurable report{impactRollup.quarter_impact?.reports_with_delta === 1 ? '' : 's'}
                </p>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-violet-100 text-xs">
                  <div>
                    <div className="text-slate-400 uppercase tracking-wider text-[10px]">Before</div>
                    <div className="font-semibold text-slate-700 tabular-nums">
                      {impactRollup.quarter_impact?.avg_before == null ? '—' : impactRollup.quarter_impact.avg_before.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 uppercase tracking-wider text-[10px]">After</div>
                    <div className="font-semibold text-violet-900 tabular-nums">
                      {impactRollup.quarter_impact?.avg_after == null ? '—' : impactRollup.quarter_impact.avg_after.toFixed(2)}
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">
                  {impactRollup.quarter_impact?.reports_posted || 0} total report{impactRollup.quarter_impact?.reports_posted === 1 ? '' : 's'} posted this quarter
                </p>
              </CardContent>
            </Card>

            {/* Recent reports */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-500" />
                  Recent Report Cards
                  <TileInfoButton
                    testid="rollup-reports-info"
                    title="Recent Report Cards"
                    body="Each row is a completed business case with a measured proficiency lift. Click a row to open it in the Business Case Builder, or the share icon to mint a public, analytics-tracked link for your CFO or board. Once live, views and CTA clicks appear as badges next to the delta so you can see which stories are landing."
                    linkLabel="Open Business Case Builder"
                    onLink={() => navigate('/business-case-builder')}
                  />
                </CardTitle>
                <p className="text-[11px] text-slate-500 mt-0.5" data-testid="rollup-reports-subtext">
                  Your latest executive stories — share any of them with stakeholders in one click.
                </p>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {impactRollup.recent_reports?.length > 0 ? (
                  impactRollup.recent_reports.map((r) => (
                    <div
                      key={r.case_id}
                      className="flex items-center gap-1 p-2 rounded-md hover:bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors"
                      data-testid={`rollup-report-row-${r.case_id}`}
                    >
                      <button
                        type="button"
                        onClick={() => navigate('/business-case-builder')}
                        className="flex-1 min-w-0 text-left"
                        data-testid={`rollup-report-${r.case_id}`}
                      >
                        <div className="text-sm text-slate-800 truncate font-medium">{r.skill_name || 'Untitled'}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                          <span>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</span>
                          {r.delta != null && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className={cn('font-semibold tabular-nums',
                                r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-rose-700' : 'text-slate-600',
                              )}>
                                {r.delta > 0 ? '+' : ''}{r.delta.toFixed(2)}
                              </span>
                            </>
                          )}
                          {r.share_views > 0 && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span
                                className="inline-flex items-center gap-1 text-indigo-600 font-medium"
                                title={`${r.share_views} view${r.share_views === 1 ? '' : 's'} across ${r.share_active_links || 1} share link${r.share_active_links === 1 ? '' : 's'}`}
                                data-testid={`rollup-report-views-${r.case_id}`}
                              >
                                <Eye className="h-3 w-3" />
                                {r.share_views}
                                {r.share_cta_clicks > 0 && (
                                  <span className="text-emerald-600 ml-1">· {r.share_cta_clicks} CTA</span>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShareTarget(r); }}
                        className="flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        title="Share with CFO/board"
                        aria-label={`Share ${r.skill_name || 'report card'}`}
                        data-testid={`rollup-report-share-${r.case_id}`}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic py-2">No report cards posted yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Engagement Leaders — Top 3 most-shared report cards (last 30 days) */}
      {impactRollup?.engagement_leaders?.length > 0 && (
        <div data-testid="engagement-leaders-widget">
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-900">Most-Engaged Report Cards</h2>
            </div>
            <span className="text-xs text-slate-400">Last 30 days · ranked by stakeholder engagement</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Visible proof that L&amp;D is being <em>consumed</em> by leadership, not just authored. Each row shows real CFO/Board attention.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {impactRollup.engagement_leaders.map((leader, idx) => {
              const rankColors = ['from-amber-50 to-orange-50 border-amber-200',
                                   'from-slate-50 to-slate-100 border-slate-200',
                                   'from-orange-50 to-rose-50 border-orange-200'];
              const rankBadge = ['bg-amber-500', 'bg-slate-400', 'bg-orange-400'];
              return (
                <Card
                  key={leader.case_id}
                  className={cn('relative bg-gradient-to-br', rankColors[idx] || 'border-slate-200')}
                  data-testid={`engagement-leader-${idx}`}
                >
                  <span
                    className={cn('absolute -top-2 -left-2 h-6 w-6 rounded-full text-white text-xs font-bold flex items-center justify-center shadow-sm',
                                  rankBadge[idx] || 'bg-slate-400')}
                  >
                    {idx + 1}
                  </span>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-slate-900 truncate" title={leader.title}>
                      {leader.skill_name || leader.title || 'Untitled'}
                    </p>
                    <div className="flex items-baseline gap-3 mt-1.5">
                      <div>
                        <p className="text-2xl font-bold text-slate-800 tabular-nums leading-none">{leader.views}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">views</p>
                      </div>
                      {leader.ctas > 0 && (
                        <div>
                          <p className="text-lg font-bold text-emerald-700 tabular-nums leading-none">{leader.ctas}</p>
                          <p className="text-[10px] text-emerald-700 uppercase tracking-wider mt-0.5">CTA</p>
                        </div>
                      )}
                      {leader.warm_intros > 0 && (
                        <div>
                          <p className="text-lg font-bold text-violet-700 tabular-nums leading-none">{leader.warm_intros}</p>
                          <p className="text-[10px] text-violet-700 uppercase tracking-wider mt-0.5">warm intros</p>
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-600 mt-2 leading-snug">
                      Reached <strong>{leader.stakeholder_count || leader.links}</strong> stakeholder{(leader.stakeholder_count || leader.links) === 1 ? '' : 's'}
                      {leader.stakeholders?.length > 0 && (
                        <span className="text-slate-400"> · {leader.stakeholders.slice(0, 3).join(', ')}{leader.stakeholders.length > 3 ? '…' : ''}</span>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleExportPptx(leader.case_id, leader.skill_name)}
                      disabled={exportingId === leader.case_id}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-white border border-slate-300 hover:border-indigo-300 text-xs font-medium text-slate-700 hover:text-indigo-700 transition-colors disabled:opacity-50"
                      data-testid={`engagement-leader-export-${idx}`}
                      title="Generate a single QBR-ready slide for this report card"
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      {exportingId === leader.case_id ? 'Generating…' : 'Export to PowerPoint'}
                    </button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}


      {/* Live Hot Prospects — recent CTA clicks (last 7d) */}
      {impactRollup?.live_hot_prospects?.length > 0 && (
        <div data-testid="live-hot-prospects-widget" className="rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-orange-50/40 p-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-rose-600" />
              <h2 className="text-lg font-bold text-slate-900">Live Hot Prospects</h2>
              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                Live
              </span>
            </div>
            <div className="flex items-center gap-3">
              {notifyPermission !== 'unsupported' && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!('Notification' in window)) return;
                    if (Notification.permission === 'granted') {
                      // Quick re-prompt acknowledgement: fire a sample alert.
                      try {
                        new Notification('Live Hot Prospects alerts active', {
                          body: 'You\'ll get a ping the moment a new CTA lands.',
                          icon: '/favicon.ico',
                        });
                      } catch (_) { /* ignore */ }
                      return;
                    }
                    if (Notification.permission === 'denied') return;
                    const result = await Notification.requestPermission();
                    setNotifyPermission(result);
                  }}
                  disabled={notifyPermission === 'denied'}
                  className={cn(
                    'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border transition-colors',
                    notifyPermission === 'granted'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                      : notifyPermission === 'denied'
                        ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100',
                  )}
                  data-testid="live-hp-notify-toggle"
                  title={
                    notifyPermission === 'granted'
                      ? 'Browser alerts are active. Click to test.'
                      : notifyPermission === 'denied'
                        ? 'Browser alerts blocked. Re-enable in browser site settings.'
                        : 'Get a native browser ping the moment a new CTA lands.'
                  }
                >
                  {notifyPermission === 'granted' ? (
                    <><Bell className="h-3 w-3" /> Alerts on</>
                  ) : notifyPermission === 'denied' ? (
                    <><BellOff className="h-3 w-3" /> Alerts blocked</>
                  ) : (
                    <><Bell className="h-3 w-3" /> Enable alerts</>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSlackPanelOpen((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border transition-colors',
                  slackUrlSaved
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100',
                )}
                data-testid="live-hp-slack-toggle"
                title="Get a Slack or Teams DM the moment a CTA lands — even when this tab is closed."
              >
                {slackUrlSaved ? '🟢 Slack/Teams' : '⚙️ Slack/Teams'}
              </button>
              <span className="text-xs text-slate-400">Last 7 days · auto-refreshes every 30s</span>
            </div>
          </div>
          {slackPanelOpen && (
            <div className="mb-3 p-3 rounded-lg bg-white border border-slate-200" data-testid="live-hp-slack-panel">
              <p className="text-xs font-semibold text-slate-700 mb-1">Slack / Microsoft Teams webhook</p>
              <p className="text-[11px] text-slate-500 mb-2 leading-snug">
                Paste an incoming-webhook URL and we'll DM you the moment a stakeholder clicks a CTA — even when your browser is closed.
                {' '}<a className="underline text-indigo-600 hover:text-indigo-800" href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer">Slack guide</a>
                {' · '}<a className="underline text-indigo-600 hover:text-indigo-800" href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook" target="_blank" rel="noreferrer">Teams guide</a>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  value={slackUrl}
                  onChange={(e) => setSlackUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/... or https://*.webhook.office.com/..."
                  className="flex-1 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                  data-testid="live-hp-slack-url-input"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSaveSlackWebhook}
                    disabled={slackBusy || slackUrl.trim() === slackUrlSaved.trim()}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    data-testid="live-hp-slack-save"
                  >
                    {slackBusy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={handleTestSlackWebhook}
                    disabled={slackBusy || !slackUrlSaved}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="live-hp-slack-test"
                  >
                    Test
                  </button>
                </div>
              </div>
              {slackMsg && (
                <p className={cn('text-[11px] mt-2', slackMsg.ok ? 'text-emerald-700' : 'text-rose-700')}>
                  {slackMsg.text}
                </p>
              )}
            </div>
          )}
          <p className="text-xs text-slate-600 mb-3">The room is warm right now. Each row is a stakeholder who didn't just open your report — they took an action. Reach out within 24 hours while the report is still top of mind.</p>
          {impactRollup?.hot_prospect_conversion?.total_clicks > 0 && (
            <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hot-prospect-conversion-kpi">
              {(() => {
                const c = impactRollup.hot_prospect_conversion;
                const oc = c.outcomes || {};
                const ratePct = c.conversion_rate != null
                  ? `${Math.round(c.conversion_rate * 100)}%`
                  : '—';
                return (
                  <>
                    <div className="px-3 py-2 rounded-lg bg-white border border-slate-200">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hot-prospect→meeting</p>
                      <p className="text-xl font-bold text-rose-700 tabular-nums leading-none mt-1">{ratePct}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">last {c.window_days || 30}d · {c.marked_count || 0}/{c.total_clicks || 0} marked</p>
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Meetings booked</p>
                      <p className="text-xl font-bold text-emerald-800 tabular-nums leading-none mt-1">{oc.meeting_booked || 0}</p>
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200">
                      <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Followed up</p>
                      <p className="text-xl font-bold text-indigo-800 tabular-nums leading-none mt-1">{oc.followed_up || 0}</p>
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">No response</p>
                      <p className="text-xl font-bold text-slate-700 tabular-nums leading-none mt-1">{oc.no_response || 0}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          <div className="space-y-2">
            {impactRollup.live_hot_prospects.map((hp, idx) => {
              const ts = hp.last_cta_at ? new Date(hp.last_cta_at) : null;
              const flashKey = `${hp.case_id}|${hp.last_cta_at}`;
              const isFlashing = hpFlashKeys.has(flashKey);
              let ago = '';
              if (ts) {
                const mins = Math.floor((Date.now() - ts.getTime()) / 60000);
                if (mins < 1) ago = 'just now';
                else if (mins < 60) ago = `${mins}m ago`;
                else if (mins < 60 * 24) ago = `${Math.floor(mins / 60)}h ago`;
                else ago = `${Math.floor(mins / (60 * 24))}d ago`;
              }
              return (
                <div
                  key={`${hp.case_id}-${idx}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-rose-100 hover:border-rose-300 hover:shadow-sm transition-all',
                    isFlashing && 'ring-2 ring-rose-400 ring-offset-1 shadow-md animate-pulse',
                  )}
                  data-testid={`live-hot-prospect-${idx}`}
                >
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-rose-100 to-orange-100 border border-rose-200 flex items-center justify-center">
                    <Flame className="h-4 w-4 text-rose-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      <span className="text-rose-700">{hp.recipient_label}</span>
                      <span className="text-slate-500 font-normal"> clicked the CTA on </span>
                      <span title={hp.title}>{hp.skill_name || hp.title || 'a report card'}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {hp.cta_click_count} CTA{hp.cta_click_count !== 1 ? 's' : ''} · {hp.view_count} view{hp.view_count !== 1 ? 's' : ''} · {ago}
                      {hp.owner_email && (
                        <span className="text-slate-400"> · owned by {hp.owner_email.split('@')[0]}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {hp.outcome ? (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded',
                          hp.outcome === 'meeting_booked' && 'bg-emerald-100 text-emerald-800 border border-emerald-300',
                          hp.outcome === 'followed_up' && 'bg-indigo-100 text-indigo-800 border border-indigo-300',
                          hp.outcome === 'no_response' && 'bg-slate-200 text-slate-700 border border-slate-300',
                        )}
                        title={hp.outcome_set_at ? `Marked ${new Date(hp.outcome_set_at).toLocaleString()}` : ''}
                      >
                        {hp.outcome === 'meeting_booked' && '✅ Meeting booked'}
                        {hp.outcome === 'followed_up' && '✉️ Followed up'}
                        {hp.outcome === 'no_response' && '— No response'}
                      </span>
                    ) : hp.owner_was_notified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                        <CheckCircle2 className="h-3 w-3" />
                        Owner notified
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                        Pending
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <select
                        value={hp.outcome || ''}
                        onChange={(e) => handleSetHotProspectOutcome(hp.token, e.target.value || null)}
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-700 hover:border-rose-300 focus:outline-none focus:ring-1 focus:ring-rose-300"
                        data-testid={`live-hot-prospect-outcome-${idx}`}
                        title="Mark the outcome of this hot prospect"
                      >
                        <option value="">Mark outcome…</option>
                        <option value="followed_up">✉️ Followed up</option>
                        <option value="meeting_booked">✅ Meeting booked</option>
                        <option value="no_response">— No response</option>
                      </select>
                      <a
                        href={hp.owner_email ? `mailto:${hp.owner_email}?subject=${encodeURIComponent(`Hot prospect on ${hp.skill_name || 'your report card'} — let's follow up`)}` : '#'}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-800"
                        data-testid={`live-hot-prospect-followup-${idx}`}
                        title="Open email to follow up with this stakeholder"
                      >
                        <Phone className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
      </CollapsibleSection>
      )}

      {/* ── OPERATIONS & COACHING ── role-specific operational widgets
          (tenant triage, weak links, coaching, digests, individual
          focus), folded into one collapsed strip. */}
      {activeHomeTab === 'health' && (
      <CollapsibleSection
        storageKey="home.operations"
        eyebrow="Operate"
        title="Operations & Coaching"
        defaultExpanded
        onDark
        description="Tenant health triage, cross-engine weak links, coaching commitments, digest schedules and individual focus."
        accent="text-cyan-700"
        testId="home-operations"
      >
      <div className={cn('space-y-8', isDark && 'home-dark-scope')}>
      {isSuperAdmin && <PlatformHealthTriageWidget />}
      {isOrgAdmin && !isSuperAdmin && <OrgHealthTriageWidget />}
      {isOrgAdmin && !isSuperAdmin && <OrgWinsStrip />}
      <WeakLinksRollupTile />
      <CoachingCommitmentsTile />
      <DigestHealthWidget />
      <PersonalImpactEntryTile navigate={navigate} />
      </div>
      </CollapsibleSection>
      )}






      {/* Mission Control Home footer — brand + Powered by Mission AI +
          Data Confidence pill, matching the restyled mockup. */}
      <HomeFooter
        dataConfidence={leiPillars && leiPillars.length > 0 ? 'High' : 'Medium'}
      />

      {/* Share Report Card dialog */}
      <ShareReportCardDialog
        open={!!shareTarget}
        onOpenChange={(o) => { if (!o) setShareTarget(null); }}
        report={shareTarget}
      />
    </div>
    {/* Constraint fix-plan panel — opened from the chain's PRIMARY
        CONSTRAINT badge. Shows the top actions to unblock that stage. */}
    <Sheet open={!!playbook} onOpenChange={(o) => { if (!o) { setPlaybook(null); setAiPlan(null); } }}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0b1220] border-l border-[#22304d] text-slate-100 overflow-y-auto" data-testid="constraint-playbook-sheet">
        {playbook && (
          <>
            <SheetHeader className="text-left">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/60 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-300 w-fit">
                <AlertTriangle className="h-3 w-3" /> Primary Constraint
              </span>
              <SheetTitle className="text-slate-100 text-xl mt-2">{playbook.stage.label} is your value bottleneck</SheetTitle>
              <SheetDescription className="text-slate-400">
                A {playbook.dropPts}-point step-down from {CONSTRAINT_PLAYBOOK[playbook.key]?.prev}. Fixing this stage recovers the most value across the chain.{constraintStreakMonths >= 1 ? ` It's held the constraint for ~${constraintStreakMonths} month${constraintStreakMonths === 1 ? '' : 's'}.` : ''}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#44b6d2] mb-3">Recommended fix-plan</p>
              <ol className="space-y-3">
                {(() => {
                  const meta = CONSTRAINT_PLAYBOOK[playbook.key] || { actions: [], prev: '' };
                  const recovery = Math.max(1, Math.round(playbook.dropPts * (maturityCeilingFactor || 1)));
                  const rootName = constraintPillarId ? (maturityPillarById[constraintPillarId]?.name || constraintShort) : null;
                  const live = [];
                  if (rootName) live.push(`Your weakest driver feeding this stage is ${rootName}${constraintScore != null ? ` (${constraintScore}/100)` : ''} — make lifting it the first move.`);
                  live.push(`This stage is losing ${playbook.dropPts} pts of value versus ${meta.prev}. Recovering ~${recovery} pts here unblocks the most value downstream.`);
                  const actions = [...live, ...meta.actions.slice(0, 2)];
                  return actions.map((a, i) => (
                    <li key={i} className="flex gap-3 text-sm text-slate-200" data-testid={`constraint-playbook-action-${i}`}>
                      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#44b6d2]/15 text-[#44b6d2] font-bold text-xs flex items-center justify-center">{i + 1}</span>
                      <span className="leading-snug">{a}</span>
                    </li>
                  ));
                })()}
              </ol>
            </div>
            {(() => {
              const sk = { capability: 'pe', confidence: 'confidence', application: 'application', behavior: 'roe' }[playbook.key];
              const days = range === '30d' ? 30 : range === '90d' ? 90 : 365;
              const rows = (Array.isArray(healthSeries) ? healthSeries : []).filter((p) => p && p[sk] != null && p.date);
              const lastD = rows.length ? new Date(rows[rows.length - 1].date) : null;
              const cut = lastD ? new Date(lastD.getTime() - days * 86400000) : null;
              const vals = rows.filter((p) => !cut || new Date(p.date) >= cut).map((p) => Number(p[sk])).filter((v) => !Number.isNaN(v));
              if (vals.length < 2) return null;
              const min = Math.min(...vals); const max = Math.max(...vals); const rng = max - min || 1;
              const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${30 - ((v - min) / rng) * 28 - 1}`).join(' ');
              const rangeLabel = range === '30d' ? 'last 30 days' : range === '90d' ? 'last 90 days' : 'last 12 months';
              return (
                <div className="mt-6" data-testid="constraint-playbook-trend">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#44b6d2] mb-2">{playbook.stage.label} score — {rangeLabel}</p>
                  <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="w-full h-14" aria-hidden="true">
                    <polyline fill="none" stroke="#44b6d2" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts} vectorEffect="non-scaling-stroke" />
                  </svg>
                  <p className="text-[11px] text-slate-500 mt-1">{Math.round(vals[0])} → {Math.round(vals[vals.length - 1])} over the {rangeLabel}</p>
                </div>
              );
            })()}
            <div className="mt-6 border-t border-[#22304d] pt-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#44b6d2]">AI fix-plan</p>
                <button
                  type="button"
                  onClick={generateAiPlan}
                  disabled={aiLoading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#44b6d2]/50 px-3 py-1 text-[11px] font-bold text-[#44b6d2] hover:bg-[#44b6d2]/10 disabled:opacity-50 transition-colors"
                  data-testid="constraint-playbook-generate-ai"
                >
                  <Sparkles className="h-3 w-3" /> {aiLoading ? 'Generating…' : aiPlan ? 'Regenerate' : 'Generate AI plan'}
                </button>
              </div>
              {aiLoading && (
                <p className="mt-3 text-sm text-slate-400" data-testid="constraint-playbook-ai-loading">Asking the narrative engine for tailored actions…</p>
              )}
              {aiPlan && !aiLoading && (
                <ul className="mt-3 space-y-2" data-testid="constraint-playbook-ai-plan">
                  {aiPlan.map((a, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-200" data-testid={`constraint-playbook-ai-action-${i}`}>
                      <Sparkles className="h-3.5 w-3.5 text-[#44b6d2] mt-0.5 shrink-0" />
                      <span className="leading-snug">{a}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={() => { const [p, q] = playbook.stage.path.split('?'); setPlaybook(null); navTo(p, q || ''); }}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#44b6d2] px-5 py-2.5 text-sm font-bold text-[#04222b] hover:translate-x-0.5 transition-transform"
              data-testid="constraint-playbook-open-stage"
            >
              Open {playbook.stage.label} Health <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
      </SheetContent>
    </Sheet>
    {/* Floating "Take the Tour" launcher + tour overlay.
        Lives outside the main content div so it can use position:fixed
        without inheriting any scroll/transform context. */}
    <MissingMiddleTour />
    </PageShell>
  );
}


